import {
  ShowRowBadges,
  ShowRowBuyButton,
  ShowRowCardWrapper,
  ShowRowComedianName,
  ShowRowDate,
  ShowRowLocation,
  ShowRowShareButton,
  ShowRowTimestampMetadata,
  ShowRowUsersChecking,
  ShowRowVenue,
} from "@/components/shows";

import ComedianAvatarWithFollowCircle from "@/components/signin/SuggestedComediansForm/ComedianAvatarWithFollowCircle";
import { MetadataBadge } from "@punchup/ui-common";
import { MultipleComediansDrawer } from "@punchup/venues/client";
import { ShowRowDirectProps } from "@/components/shows/types";
import { useShowRowContext } from "@/components/shows/ShowRowContext";

export function ShowCard({ showRef }: ShowRowDirectProps) {
  const {
    shows,
    comedian,
    showcaseComedians,
    isFollowed,
    followedComedianIds,
    hasBadges,
    hasPresaleCode,
    getImageUrl,
    hideComedianName,
    hideComedianAvatar,
    isComediansDrawerOpen,
    setIsComediansDrawerOpen,
  } = useShowRowContext();

  const show = shows[0];
  const isShowcase = showcaseComedians && showcaseComedians.length > 1;

  // Determine max avatars to show for mobile
  const maxAvatarsToShow = isShowcase ? 5 : 2;

  if (isShowcase) {
    return (
      <>
        <ShowRowCardWrapper>
          <div className="grid items-center gap-y-5 px-3 py-5">
            {/* Avatars section - conditionally shown */}
            {!hideComedianAvatar && (
              <div className="flex items-center justify-between gap-x-6">
                <div className="flex -space-x-5">
                  {showcaseComedians
                    .slice(0, maxAvatarsToShow)
                    .map((com, index) => (
                      <div
                        key={com.id}
                        className="relative"
                        style={{ zIndex: maxAvatarsToShow - index }}
                      >
                        <ComedianAvatarWithFollowCircle
                          comedianId={com.id}
                          comedianImgUrl={getImageUrl(com)}
                          comedianName={com.display_name || ""}
                          comedianSlug={com.slug}
                          size="lg"
                          className="relative"
                          isFollowed={followedComedianIds.includes(com.id)}
                          isNotPunchupComedian={!com.is_punchup_verified}
                          hideComedianName
                          hideFollowIndicator={
                            index === maxAvatarsToShow - 1 &&
                            showcaseComedians.length > maxAvatarsToShow
                          }
                        />
                        {index === maxAvatarsToShow - 1 &&
                          showcaseComedians.length > maxAvatarsToShow && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setIsComediansDrawerOpen(true);
                              }}
                              className="border-[1.5px] border-primary w-full h-full left-0 top-0 absolute bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center z-10 transition-colors duration-200 cursor-pointer"
                            >
                              <div className="flex gap-1 group">
                                <div className="w-1.5 h-1.5 rounded-full bg-white group-hover:scale-110 transition-transform" />
                                <div className="w-1.5 h-1.5 rounded-full bg-white group-hover:scale-110 transition-transform" />
                                <div className="w-1.5 h-1.5 rounded-full bg-white group-hover:scale-110 transition-transform" />
                              </div>
                            </button>
                          )}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Date, title and comedian names section */}
            <div
              ref={showRef}
              className="flex flex-col flex-wrap justify-start text-lg font-bold text-primary-blue"
            >
              <div className="flex flex-col pb-3 gap-y-1">
                <ShowRowDate />
                <ShowRowLocation />
              </div>

              <div className="pb-[10px]">
                {show.title ? (
                  <p className="text-lg font-bold text-primary-blue">
                    {show.title}
                  </p>
                ) : (
                  <p className="text-lg font-bold text-primary-blue">
                    {showcaseComedians
                      .slice(0, 2)
                      .map((c) => c.display_name)
                      .join(", ")}
                    {showcaseComedians.length > 2 && (
                      <span> +{showcaseComedians.length - 2}</span>
                    )}
                  </p>
                )}
              </div>

              {/* Venue information */}
              <ShowRowVenue />
            </div>

            {/* Badges and users checking */}
            {(hasBadges || hasPresaleCode) && (
              <div className="flex flex-row justify-start">
                <div className="flex flex-col items-center gap-2 w-full">
                  <ShowRowBadges showId={show.id} />
                  <ShowRowUsersChecking showId={show.id} />
                  {hasPresaleCode && (
                    <div className="md:pr-4">
                      <p className="normal-case font-normal">
                        <MetadataBadge
                          text={`CODE: ${show.presaleCode.trim()}`}
                        />
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Buy button section */}
            <div className="flex w-full">
              <ShowRowBuyButton />
            </div>
          </div>
        </ShowRowCardWrapper>

        {/* Multiple comedians drawer for showcase shows */}
        <MultipleComediansDrawer
          isOpen={isComediansDrawerOpen}
          setIsOpen={setIsComediansDrawerOpen}
          comedians={showcaseComedians}
          followedComedians={followedComedianIds}
        />
      </>
    );
  }

  // Regular single-comedian mobile view
  return (
    <>
      <ShowRowCardWrapper className="block rounded-lg shadow-[0px_0px_4px_0px_rgba(0,0,0,0.25)]">
        <div className="grid items-center gap-y-3 p-4">
          {/* Avatar section - conditionally shown */}
          {!hideComedianAvatar && (
            <div className="flex items-center justify-center">
              <ComedianAvatarWithFollowCircle
                comedianId={comedian.id}
                comedianImgUrl={getImageUrl(comedian)}
                comedianName={comedian.display_name || ""}
                comedianSlug={comedian.slug}
                size="lg"
                className="relative"
                hideComedianName
                isFollowed={isFollowed}
                isNotPunchupComedian={!comedian.is_punchup_verified}
              />
            </div>
          )}

          {/* Date, comedian name and venue section */}
          <div
            ref={showRef}
            className="flex flex-col flex-wrap justify-start text-lg font-bold text-primary-blue"
          >
            <div className="flex flex-row gap-x-1 w-full justify-between">
              <div className="flex flex-col pb-3 gap-y-1 flex-1">
                <ShowRowDate />
                <ShowRowLocation />
              </div>
              <div className="flex flex-col justify-start items-center -m-1">
                <ShowRowShareButton />
              </div>
            </div>

            {hideComedianName ? (
              <ShowRowTimestampMetadata />
            ) : (
              <div className="pb-[10px]">
                <ShowRowComedianName className="text-lg font-bold text-primary-blue line-clamp-1" />
              </div>
            )}
          </div>

          {/* Badges and users checking */}
          {(hasBadges || hasPresaleCode) && (
            <div className="flex flex-row justify-start">
              <div className="flex flex-col items-center gap-2 w-full">
                <ShowRowBadges showId={show.id} />
                <ShowRowUsersChecking showId={show.id} />
                {hasPresaleCode && (
                  <div className="md:pr-4 w-full">
                    <p className="normal-case font-normal">
                      <MetadataBadge
                        className="w-full"
                        text={`CODE: ${show.presaleCode.trim()}`}
                      />
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Buy button section */}
          <div className="flex w-full">
            <ShowRowBuyButton />
          </div>
        </div>
      </ShowRowCardWrapper>

      {/* Multiple comedians drawer for showcase shows */}
      {showcaseComedians && showcaseComedians.length > 1 && (
        <MultipleComediansDrawer
          isOpen={isComediansDrawerOpen}
          setIsOpen={setIsComediansDrawerOpen}
          comedians={showcaseComedians}
          followedComedians={followedComedianIds}
        />
      )}
    </>
  );
}
